// Hideo - the "@" menu of the fields with mentions (.mention-field: legacy and Vue fields alike, MentionField.vue), drawn
// into the static #mentionMenu of index.html (z-96). Ported from the legacy ui/mentions.js with the same HTML and the
// same moments (golden tests: tests/mentions.test.ts against tests/fixtures/legacy/mentions-w11.js).
// installMentions(doc) adds the document listeners the legacy file added when the bundle loaded (input, keydown in the
// capture phase, paste, focusout): src/main.ts calls it once, right after loadApp and before the script editor's
// surface, in the same order as before (docs/architecture.md, "Global keyboard shortcuts"), then the listeners that replace
// inline handlers: the menu's rows (data-pick) and the clickable chips (data-open, src/features/mentions.ts).
import { t } from '../i18n'
import { esc } from '../utils/html'
import { MENTION_TYPES, chipHtml, mentionCandidates, openChip, type MentionItem } from './mentions'
import { deleteTyped, placeAtCursor, typedBefore } from './editor/caretMenu'

const menuEl = () => document.getElementById('mentionMenu') as HTMLElement
/** The menu's state: open, highlighted row, items shown, the text typed after "@", the field */
const mention = { open: false, idx: 0, items: [] as MentionItem[], q: '', field: null as HTMLElement | null }

export function mentionClose(): void { mention.open = false; menuEl().classList.add('hidden') }
function mentionRender(): void {
  menuEl().innerHTML = mention.items.map(([type, , name], i) => `<div data-pick="${i}" class="flex items-center gap-3 px-2.5 py-2 rounded-lg cursor-pointer text-sm ${i === mention.idx ? 'bg-darkbtn' : ''}">
        <i class="fas ${MENTION_TYPES[type].icon} w-5 text-center text-brand"></i><span class="min-w-0 flex-1 truncate">${esc(name)}</span><span class="text-[10px] text-gray-500 flex-shrink-0">${t(MENTION_TYPES[type].label)}</span></div>`).join('')
}
/** After typing in a field: "@" at the start or after a space, followed by letters, opens (or filters) the menu */
export function mentionCheck(field: HTMLElement): void {
  const m = typedBefore(field, /(?:^|\s)@([\wàèéìòù]*)$/i)
  if (!m) return mentionClose()
  mention.q = m[1].toLowerCase(); mention.field = field
  // "@" followed by a type name lists that type, in either language
  const typeMatch = (type: MentionItem[0]) => { const l = MENTION_TYPES[type].label; return [l, t(l)].some(x => x.toLowerCase().startsWith(mention.q)) }
  mention.items = mentionCandidates().filter(([type, , name]) => (name || '').toLowerCase().includes(mention.q) || typeMatch(type)).slice(0, 12)
  if (!mention.items.length) return mentionClose()
  if (!mention.open) mention.idx = 0
  mention.idx = Math.min(mention.idx, mention.items.length - 1); mention.open = true
  const mn = menuEl()
  mentionRender(); mn.classList.remove('hidden')
  placeAtCursor(mn, () => field.getBoundingClientRect(), 290)
}
/** Replaces "@text" with the label and puts the caret right after it */
export function mentionPick(i: number): void {
  const it = mention.items[i]
  if (!it) return
  const s = getSelection() as Selection, r = deleteTyped(mention.q)
  const tpl = document.createElement('template'); tpl.innerHTML = chipHtml(it[0], it[1], false)
  const chip = tpl.content.firstChild as Node, space = document.createTextNode('\u00a0')
  r.insertNode(space); r.insertNode(chip)
  const after = document.createRange(); after.setStartAfter(space); after.collapse(true); s.removeAllRanges(); s.addRange(after)
  mentionClose()
}

const fieldOf = (e: Event): HTMLElement | null => {
  const tg = e.target as Element | null
  return tg && tg.closest ? tg.closest<HTMLElement>('.mention-field') : null
}
let installed = false
/** The document listeners of the fields' "@" menu and paste (once; the order is the legacy one), then the menu's mousedown
 *  (a row is picked without the field losing the cursor: preventDefault) and the chips' click (document, capture phase:
 *  before the card or row around the chip, which never gets it) */
export function installMentions(doc: Pick<Document, 'addEventListener'> = document): void {
  if (installed) return
  installed = true
  doc.addEventListener('input', e => { const f = fieldOf(e); if (f) mentionCheck(f) })
  doc.addEventListener('keydown', ev => {
    const e = ev as KeyboardEvent, f = fieldOf(e)
    if (!f || !mention.open) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); mention.idx = (mention.idx + (e.key === 'ArrowDown' ? 1 : -1) + mention.items.length) % mention.items.length; mentionRender() }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); mentionPick(mention.idx) }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); mentionClose() }
  }, true)
  // Always pasted as plain text
  doc.addEventListener('paste', ev => {
    const e = ev as ClipboardEvent, f = fieldOf(e)
    if (!f) return
    e.preventDefault(); document.execCommand('insertText', false, (e.clipboardData as DataTransfer).getData('text/plain'))
  })
  doc.addEventListener('focusout', e => { if (fieldOf(e)) setTimeout(mentionClose, 150) })
  const menu = menuEl()
  menu.addEventListener('mousedown', e => {
    const row = (e.target as Element).closest<HTMLElement>('[data-pick]')
    if (row && menu.contains(row)) { e.preventDefault(); mentionPick(Number(row.dataset.pick)) }
  })
  doc.addEventListener('click', openChip, true)
}
/** For the golden tests: installs again on another target (the state is shared) */
export const _resetMentionsInstall = () => { installed = false }
